
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserRole } from '@/types/user';
import { 
  User, 
  UserRound,
  Users, 
  Landmark, 
  Camera,
  Trophy,
  Clipboard,
  Heart,
  Crown
} from 'lucide-react';

interface RoleCardProps {
  role: UserRole;
  title: string;
  description: string;
  features: string[];
  accountTier?: 'free' | 'paid';
}

const getRoleIcon = (role: UserRole) => {
  switch (role) {
    case 'player':
      return <User className="h-8 w-8 text-sports-green" />;
    case 'parent':
      return <UserRound className="h-8 w-8 text-sports-green" />;
    case 'coach':
      return <Users className="h-8 w-8 text-sports-green" />;
    case 'venue':
      return <Landmark className="h-8 w-8 text-sports-green" />;
    case 'creative':
      return <Camera className="h-8 w-8 text-sports-green" />;
    case 'team-owner':
      return <Trophy className="h-8 w-8 text-sports-green" />;
    case 'support':
      return <Clipboard className="h-8 w-8 text-sports-green" />;
    case 'fan':
      return <Heart className="h-8 w-8 text-sports-green" />;
    default:
      return <User className="h-8 w-8 text-sports-green" />;
  }
};

const RoleCard: React.FC<RoleCardProps> = ({ role, title, description, features, accountTier }) => {
  const icon = getRoleIcon(role);
  
  return (
    <Card className="flex flex-col h-full hover:shadow-lg transition-shadow bg-gray-800 border-gray-700">
      <CardHeader className="pb-4">
        <div className="mb-4 flex items-center justify-between">
          {icon}
          {accountTier && (
            <Badge 
              variant={accountTier === 'paid' ? 'default' : 'outline'}
              className={accountTier === 'paid' 
                ? 'bg-yellow-400 text-black' 
                : 'border-sports-green text-sports-green'
              }
            >
              {accountTier === 'paid' && <Crown className="w-3 h-3 mr-1" />}
              {accountTier === 'paid' ? 'Paid' : 'Free'}
            </Badge>
          )}
        </div>
        <CardTitle className="text-xl text-white">{title}</CardTitle>
        <CardDescription className="text-gray-300">{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <span className="mr-2 text-sports-green">✓</span>
              <span className="text-sm text-gray-300">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button asChild className="w-full bg-sports-green text-black hover:bg-sports-green/90 font-bold">
          <Link to={`/register?role=${role}`}>Sign up as {title}</Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default RoleCard;
